import React, { useState, useEffect } from 'react';
import { useMyProvider } from '../../hooks/useMyProvider';
import { analyticsService } from '../../services/analytics';
import {
  TopicDistributionResponse,
  TopicInsightsResponse,
  TopicSortBy,
} from '../../types/analytics';
import TopicWordCloud from '../../components/Charts/TopicWordCloud';
import TopicChip from '../../components/Dashboard/TopicChip';
import NotAvailable from '../../components/Common/NotAvailable';

const Topics: React.FC = () => {
  const { provider, loading: providerLoading, error: providerError } = useMyProvider();
  const [sortBy, setSortBy] = useState<TopicSortBy>('frequency');
  const [distribution, setDistribution] = useState<TopicDistributionResponse | null>(null);
  const [insights, setInsights] = useState<TopicInsightsResponse | null>(null);
  // Starts true so the counts don't flash "Not available" before the first fetch
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (provider?.id) {
      setLoading(true);
      Promise.all([
        analyticsService.getTopicDistribution(provider.id, sortBy),
        analyticsService.getTopicInsights(provider.id),
      ])
        .then(([distData, insightData]) => {
          setDistribution(distData);
          setInsights(insightData);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [provider?.id, sortBy]);

  if (providerLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          {providerError || 'No provider profile found. Create your provider profile to see analytics.'}
        </p>
      </div>
    );
  }

  // Topic count for an insight card: a real count once insights load,
  // <NotAvailable /> when they couldn't be loaded (never a made-up 0)
  const insightCount = (count: number | undefined) =>
    loading ? (
      <div className="h-9 w-12 bg-white/60 rounded animate-pulse"></div>
    ) : count !== undefined ? (
      count
    ) : (
      <NotAvailable className="text-base" />
    );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-['Ovo'] text-2xl md:text-3xl text-[#1f1f1f] mb-2">Topic Analysis</h1>
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          Discover what patients are talking about in your reviews
        </p>
      </div>

      {/* Topic Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Positive Topics Card */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center mb-3">
            <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <h3 className="font-['Open_Sans'] text-sm font-semibold text-green-800 uppercase">
              Positive Topics
            </h3>
          </div>
          <div className="font-['Ovo'] text-3xl text-green-900 mb-2">
            {insightCount(insights?.positive_topics.length)}
          </div>
          <p className="font-['Open_Sans'] text-xs text-green-700">
            Topics with positive sentiment
          </p>
        </div>

        {/* Negative Topics Card */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-3">
            <svg className="w-6 h-6 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <h3 className="font-['Open_Sans'] text-sm font-semibold text-red-800 uppercase">
              Negative Topics
            </h3>
          </div>
          <div className="font-['Ovo'] text-3xl text-red-900 mb-2">
            {insightCount(insights?.negative_topics.length)}
          </div>
          <p className="font-['Open_Sans'] text-xs text-red-700">Topics needing attention</p>
        </div>

        {/* Emerging Topics Card */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center mb-3">
            <svg className="w-6 h-6 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <h3 className="font-['Open_Sans'] text-sm font-semibold text-yellow-800 uppercase">
              Emerging Topics
            </h3>
          </div>
          <div className="font-['Ovo'] text-3xl text-yellow-900 mb-2">
            {insightCount(insights?.emerging_topics.length)}
          </div>
          <p className="font-['Open_Sans'] text-xs text-yellow-700">New trending topics</p>
        </div>
      </div>

      {/* Topic Word Cloud */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-6">Topic Cloud</h2>
        {loading ? (
          <div className="flex items-center justify-center h-80">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
          </div>
        ) : distribution && distribution.topics.length > 0 ? (
          <TopicWordCloud topics={distribution.topics} />
        ) : (
          <div className="flex items-center justify-center h-80 text-[#767472]">
            <p className="font-['Open_Sans'] text-sm">No topic data available</p>
          </div>
        )}
      </div>

      {/* Topic Distribution Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['Ovo'] text-xl text-[#1f1f1f]">Topic Distribution</h2>

          {/* Sort Controls */}
          <div className="flex space-x-2">
            <button
              onClick={() => setSortBy('frequency')}
              className={`px-3 py-1 text-xs font-['Open_Sans'] font-medium uppercase rounded transition-colors ${
                sortBy === 'frequency'
                  ? 'bg-[#1f1f1f] text-white'
                  : 'bg-[#f8f6f2] text-[#1f1f1f] hover:bg-[#eaeaea]'
              }`}
            >
              By Frequency
            </button>
            <button
              onClick={() => setSortBy('sentiment')}
              className={`px-3 py-1 text-xs font-['Open_Sans'] font-medium uppercase rounded transition-colors ${
                sortBy === 'sentiment'
                  ? 'bg-[#1f1f1f] text-white'
                  : 'bg-[#f8f6f2] text-[#1f1f1f] hover:bg-[#eaeaea]'
              }`}
            >
              By Sentiment
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
          </div>
        ) : distribution && distribution.topics.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#eaeaea]">
                  <th className="px-4 py-3 text-left font-['Open_Sans'] text-sm font-semibold text-[#767472] uppercase">
                    Topic
                  </th>
                  <th className="px-4 py-3 text-center font-['Open_Sans'] text-sm font-semibold text-[#767472] uppercase">
                    Count
                  </th>
                  <th className="px-4 py-3 text-center font-['Open_Sans'] text-sm font-semibold text-[#767472] uppercase">
                    Avg Sentiment
                  </th>
                  <th className="px-4 py-3 text-center font-['Open_Sans'] text-sm font-semibold text-[#767472] uppercase">
                    Distribution
                  </th>
                </tr>
              </thead>
              <tbody>
                {distribution.topics.map((topic, index) => (
                  <tr key={index} className="border-b border-[#eaeaea] hover:bg-[#f8f6f2]">
                    <td className="px-4 py-3">
                      <TopicChip topic={topic.topic} sentiment={topic.avg_sentiment} />
                    </td>
                    <td className="px-4 py-3 text-center font-['Open_Sans'] text-sm text-[#1f1f1f]">
                      {topic.count}
                    </td>
                    <td className="px-4 py-3 text-center font-['Open_Sans'] text-sm text-[#1f1f1f]">
                      {topic.avg_sentiment.toFixed(3)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center space-x-2 text-xs">
                        <span className="text-green-600">+{topic.positive_mentions}</span>
                        <span className="text-yellow-600">~{topic.neutral_mentions}</span>
                        <span className="text-red-600">-{topic.negative_mentions}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-[#767472]">
            <p className="font-['Open_Sans'] text-sm">No topic data available</p>
          </div>
        )}
      </div>

      {/* Action Items */}
      {insights && insights.action_items.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <h2 className="font-['Ovo'] text-xl text-orange-900 mb-4">Recommended Actions</h2>
          <ul className="space-y-2">
            {insights.action_items.map((item, index) => (
              <li key={index} className="flex items-start">
                <svg
                  className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-['Open_Sans'] text-sm text-orange-900">{item.recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Topics;
